// ink-component
import React, { HTMLAttributes, ReactNode, useContext } from 'react';
import styled, { css, ThemeContext } from 'styled-components';

import { resetBrowserButton } from '../../../Button/styles';

import Icon from '../../../Icon/Icon';

import { newTableTokens } from '../../../../tokens/components/NewTable/tokens';
import { buttonTokens } from '../../../../tokens/components/Button/tokens';

export interface NewTablePinProps extends HTMLAttributes<HTMLSpanElement> {
  /** NewTableBody child nodes. The text in the header */
  children?: ReactNode;
  /** Defines the state of the carat icon (ascending or descending) */
  direction?: 'ascending' | 'descending';
}

const StyledPin = styled.button<NewTablePinProps>`
  ${resetBrowserButton};
  border-radius: ${newTableTokens.PinBorderRadiusBaseDefault};
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  margin: 0 -5px;
  padding: 0 5px;
  transition: all 80ms ease-out;

  &:focus-visible {
    outline: ${buttonTokens.BorderWidthBaseDefault} ${buttonTokens.BorderStyleBaseDefault}
      ${buttonTokens.BorderColorBaseKeyboardFocus};
    box-shadow: 0 0 0 ${buttonTokens.ShadowWidthBaseKeyboardFocus} ${buttonTokens.ShadowColorBaseKeyboardFocus};
  }

  &:hover {
    background-color: ${newTableTokens.PinBackgroundColorBaseHover};
  }
`;

const StyledPinIcon = styled.span`
  color: ${newTableTokens.PinForegroundColorBaseDefault};
  display: inline-flex;

  ${({ theme }) =>
    theme.direction &&
    css`
      color: ${newTableTokens.PinForegroundColorBaseDown};
    `}

  ${({ theme }) =>
    theme.cellAlign === 'right' &&
    css`
      margin-right: 4px;
    `}

  ${({ theme }) =>
    theme.cellAlign === 'left' &&
    css`
      margin-left: 4px;
    `}
`;

/**
 * @group NewTable
 * @purpose Clickable element that wraps around sortable headers
 * @categories table
 */
function NewTablePin(props: NewTablePinProps) {
  const { className, style, direction, children, ...otherProps } = props;
  const currentTheme = useContext(ThemeContext);

  const iconType = {
    ascending: 'up-carat',
    descending: 'down-carat',
  };

  const getMeaning = () => {
    if (direction === 'ascending') return 'ascending order';
    if (direction === 'descending') return 'descending order';

    return 'select column direction';
  };

  const theme = { ...currentTheme, direction };

  return (
    <StyledPin role="button" {...otherProps}>
      {(!currentTheme?.cellAlign || currentTheme?.cellAlign === 'left') && children}

      <StyledPinIcon theme={theme}>
        <Icon type={direction ? iconType[direction] : 'sort-carat'} meaning={getMeaning()} size="xxs" />
      </StyledPinIcon>

      {currentTheme?.cellAlign === 'right' && children}
    </StyledPin>
  );
}

NewTablePin.defaultProps = {
  children: undefined,
  direction: undefined,
};

NewTablePin.displayName = 'NewTable.Pin';

export default NewTablePin;
